import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { CareerChallengeSponsorDto } from '../../../../models';
import { CareerChallengeAdminStore } from '../shared/career-challenge-admin.store';
import { ImageUrlFieldComponent } from '../../../../shared/challenge-media/image-url-field.component';
import { resolveChallengeMediaSrc } from '../../../../shared/challenge-media/challenge-media.util';

@Component({
  selector: 'app-career-challenge-sponsors-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatSnackBarModule,
    ImageUrlFieldComponent
  ],
  templateUrl: './sponsors-tab.component.html',
  styleUrl: './tab-shared.scss'
})
export class CareerChallengeSponsorsTabComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CsrWebApiService);
  private readonly store = inject(CareerChallengeAdminStore);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);

  busy = false;
  editingId: string | null = null;
  editingDisplayUrl: string | null = null;

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    logoUrl: [''],
    websiteUrl: [''],
    tier: ['partner'],
    sortOrder: [0, Validators.required],
    accountId: ['']
  });

  get id(): string {
    return this.route.parent?.snapshot.paramMap.get('id') ?? '';
  }

  get sponsors(): CareerChallengeSponsorDto[] {
    return this.store.challenge()?.sponsors ?? [];
  }

  get loading() {
    return this.store.loading();
  }

  ngOnInit(): void {
    void this.store.load(this.id);
  }

  edit(sponsor: CareerChallengeSponsorDto): void {
    this.editingId = sponsor.id;
    this.form.patchValue({
      name: sponsor.name,
      logoUrl: sponsor.logoUrl ?? '',
      websiteUrl: sponsor.websiteUrl ?? '',
      tier: String(sponsor.tier ?? 'partner'),
      sortOrder: sponsor.sortOrder,
      accountId: sponsor.accountId ?? ''
    });
    this.editingDisplayUrl = sponsor.logoDisplayUrl ?? null;
  }

  reset(): void {
    this.editingId = null;
    this.editingDisplayUrl = null;
    this.form.reset({ name: '', logoUrl: '', websiteUrl: '', tier: 'partner', sortOrder: 0, accountId: '' });
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    this.busy = true;
    try {
      const raw = this.form.getRawValue();
      await firstValueFrom(
        this.api.upsertCareerChallengeSponsor(
          this.id,
          {
            name: raw.name.trim(),
            logoUrl: raw.logoUrl.trim() || null,
            websiteUrl: raw.websiteUrl.trim() || null,
            tier: raw.tier.trim() || null,
            sortOrder: Number(raw.sortOrder) || 0,
            accountId: raw.accountId.trim() || null
          },
          this.editingId ?? undefined
        )
      );
      this.snackBar.open('Sponsor saved', 'Close', { duration: 2200 });
      this.reset();
      await this.store.load(this.id, true);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  async remove(sponsorId: string): Promise<void> {
    if (!confirm('Delete this sponsor?')) {
      return;
    }
    this.busy = true;
    try {
      await firstValueFrom(this.api.deleteCareerChallengeSponsor(this.id, sponsorId));
      await this.store.load(this.id, true);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  private readError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = error.error?.detail ?? error.error?.title;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    return 'Request failed';
  }

  sponsorImageSrc(sponsor: CareerChallengeSponsorDto): string {
    return resolveChallengeMediaSrc(sponsor.logoDisplayUrl, sponsor.logoUrl, 'sponsor');
  }
}
